/**
 * Typed fetchers for /api/v1/validation/* — release validation runs.
 *
 * ⚠️ TEMPORARY LOCAL TYPES, for the same reason as `./ai.ts`: `pnpm gen:api`
 * needs the backend's dependency tree to dump `openapi.json`. The interfaces
 * mirror `backend/src/daquma/api/v1/validation.py` field-for-field; swap each
 * for `components["schemas"]["…"]` once the generator has been run.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";
import type { BlobSummary, ContainerSummary } from "./integration-browse";

type AssessmentProgressOut = components["schemas"]["AssessmentProgressOut"];

export type RunStatus =
  | "draft"
  | "assessed"
  | "risks_confirmed"
  | "planned"
  | "authored"
  | "executing"
  | "reported"
  | "approved"
  | "rejected"
  /** Off the rail (ADR-0062): the owner cancelled it; read-only until reopened. */
  | "cancelled"
  /** Off the rail (ADR-0088): closed by the owner's signature after the risk
   *  assessment — no verification required. A signed record, never reopened. */
  | "closed";

export type TestStatus =
  | "draft"
  | "approved"
  | "assigned"
  | "in_progress"
  | "passed"
  | "failed"
  | "blocked"
  /** Taken out after gate 2: kept, struck through, out of the evaluation. */
  | "withdrawn"
  /** Accepted on other evidence instead of executed (ADR-0057) — never a pass. */
  | "accepted_without_execution";

/** Why a test was accepted without execution (ADR-0057). */
export const ACCEPTANCE_CATEGORIES = [
  "supplier_testing",
  "unchanged_since_last_release",
  "covered_by_other_test",
  "other",
] as const;
export type AcceptanceCategory = (typeof ACCEPTANCE_CATEGORIES)[number];
export const ACCEPTANCE_CATEGORY_LABEL: Record<string, string> = {
  supplier_testing: "covered by supplier testing",
  unchanged_since_last_release: "unchanged since the last validated release",
  covered_by_other_test: "covered by another test",
  other: "other evidence",
};

export type TestMode = "manual" | "automatic";
export type Readiness = "go" | "go_with_conditions" | "no_go";

export interface ItqraScore {
  process_type: string | null;
  business_impact: string | null;
  use_frequency: string | null;
  process_rationale: string | null;
  impact_rationale: string | null;
  frequency_rationale: string | null;
}

export interface ItqraComputed {
  process_type: string;
  business_impact: string;
  use_frequency: string;
  weights: { process_type: number; business_impact: number; use_frequency: number };
  score: number;
  band: string;
  level: string;
}

export interface RiskItem {
  id: string;
  title: string;
  area: string | null;
  gxp_risk: string;
  category: string | null;
  /** Novo Nordisk's ITQRA buckets (from the model) and the platform-computed
   *  score/level. Null for a non-ITQRA assessment. */
  itqra: ItqraScore | null;
  itqra_computed: ItqraComputed | null;
  rationale: string | null;
  affected_objects: string[];
  source: string | null;
  confirmed: boolean;
  override_note: string | null;
  original_gxp_risk: string | null;
  /** "ai" = extracted from the assessment; "manual" = added by a person.
   *  Set by the server; edits never change it. */
  origin?: "ai" | "manual";
  added_by_user_id?: string | null;
  added_at_utc?: string | null;
  /** Resolved at view time (blank after erasure); never stored on the item. */
  added_by_display_name?: string | null;
  /** Bumped by the server on every change (ADR-0055). A write naming an older
   *  version is refused with 409 `validation_risk_stale`. */
  version: number;
  updated_by_user_id?: string | null;
  updated_at_utc?: string | null;
  /** The person responsible for this risk; may edit its rating and note. */
  assignee_user_id?: string | null;
  assigned_at_utc?: string | null;
  /** Resolved at view time (blank after erasure). */
  assignee_display_name?: string | null;
  /** The run's requirement ids this risk affects (ADR-0060). */
  requirement_refs?: string[];
  /** The acceptance criteria it affects, as "REQ-01:AC03" (ADR-0083 / ADR-0084). */
  ac_refs?: string[];
  /** The configuration rows the risk is about (ADR-0084): from the computed
   *  change list, each with its change kind and whether the RfC asked for it. */
  config_items?: { sheet: string; key: string; change: string; in_scope: boolean }[];
  /** "in" when every configuration item was asked for, "outside" when none
   *  was (a change the RfC did not say), "mixed"; absent without items. */
  scope?: "in" | "outside" | "mixed" | null;
  /** The validation method's item fields (ADR-0079): key → value; absent
   *  when the run's method has none. */
  fields?: Record<string, unknown>;
  /** The category the method's rules gave, or a person set (ADR-0079). */
  classification?: {
    category?: string | null;
    rule_id?: string | null;
    source?: "rule" | "default" | "person" | "none";
    computed?: string | null;
    override?: string | null;
    note?: string | null;
  } | null;
  /** The rating method (ADR-0079 §5.9): the catalogue row, or the level on
   *  each axis of the matrix; and what the platform rated the item on.
   *  Absent under ITQRA. */
  rating_ref?: string | null;
  rating_levels?: Record<string, string> | null;
  rating_basis?: {
    method: "catalogue" | "matrix";
    matched: boolean;
    ref?: string | null;
    title?: string | null;
    value?: string | null;
    levels?: Record<string, string>;
    reason?: string;
    fallback?: "itqra" | "rating";
    rating?: string | null;
    /** ADR-0085: the derived axes, by key, and what each was read from. */
    derived?: Record<string, string> | null;
  } | null;
}

/** One requirement of the optional Requirements Specification (ADR-0060). */
export interface Requirement {
  /** REQ-nn, numbered by the server. */
  id: string;
  text: string;
  /** A short name (ADR-0089): the catalogue's title column or the first line of
   *  its description; people may change it. */
  title?: string | null;
  /** The catalogue export's other columns, by name (ADR-0089); read-only context. */
  attributes?: Record<string, string>;
  gxp_relevant: boolean;
  source_section: string | null;
  /** "ai" = extracted from the customer's URS; "manual" = added by a person;
   *  "import" = from the customer's requirement list (ADR-0079). */
  origin: "ai" | "manual" | "import";
  /** The customer's own id for the requirement, e.g. its TIMS id (ADR-0079). */
  external_id?: string | null;
  /** The RfC(s) it relates to, when matched to the run's change (ADR-0080). */
  rfc?: string | null;
  added_by_user_id?: string | null;
  added_at_utc?: string | null;
  /** Resolved at view time (blank after erasure). */
  added_by_display_name?: string | null;
  /** From the customer's catalogue (ADR-0083): type, classification, version. */
  req_type?: string | null;
  classification?: string | null;
  version?: string | null;
  /** The acceptance criteria, each with whether the change touches it. */
  acceptance_criteria?: AcceptanceCriterion[];
  /** How it got on the run: id_exact, id_version, user_story, work_item,
   *  possible, ai_suggested, manual; absent for an extracted requirement. */
  match_basis?: string | null;
  match_detail?: string | null;
  catalogue_row?: number | null;
  /** ADR-0095: how relevant the match rated it to the change, 0-100 (100 for
   *  a row the change names by id); absent when typed by a person. */
  relevance?: number | null;
}

/** One acceptance criterion of a requirement (ADR-0083). */
export interface AcceptanceCriterion {
  id: string;
  text: string;
  selected: boolean;
}

/** How a requirement got on the run (ADR-0083), as shown. */
export const BASIS_LABEL: Record<string, { label: string; color: string }> = {
  id_exact: { label: "Named in the change", color: "green" },
  id_version: { label: "Named (other version)", color: "green" },
  user_story: { label: "Via user story", color: "teal" },
  work_item: { label: "Via work item", color: "teal" },
  possible: { label: "Possible, confirm", color: "orange" },
  ai_suggested: { label: "Suggested by AI", color: "blue" },
  manual: { label: "Added by a person", color: "gray" },
};

/** An edit of the requirement list: no `id` adds one; a stored id left out is removed. */
export interface RequirementIn {
  id?: string | null;
  text: string;
  gxp_relevant: boolean;
  source_section?: string | null;
  external_id?: string | null;
  rfc?: string | null;
  /** Which criteria the change touches (ADR-0083); omitted keeps them. */
  acceptance_criteria?: { id: string; selected: boolean }[] | null;
  /** The type and risk classification (ADR-0085); omitted keeps them, "" clears. */
  req_type?: string | null;
  classification?: string | null;
  /** The short name (ADR-0089); omitted keeps it, "" clears. */
  title?: string | null;
}

/** How a test stands in the Test Plan's matrix. Only `live` and
 *  `accepted_counted` cover a requirement or a risk. */
export type TraceTestState = "live" | "withdrawn" | "accepted_counted" | "accepted_pending_qa";

export interface TraceTest {
  code: string;
  state: TraceTestState;
  via: "risk" | "requirement";
  /** The requirement's acceptance criteria this test names (ADR-0086). */
  criteria?: string[];
}

/** A selected acceptance criterion of a requirement and the tests naming it. */
export interface TraceCriterion {
  id: string;
  tests: string[];
  covered: boolean;
}

/** The requirement → risk → test matrix, computed by the server (ADR-0060). */
export interface Traceability {
  has_requirements: boolean;
  requirements: {
    id: string;
    external_id?: string | null;
    text: string | null;
    gxp_relevant: boolean;
    risks: string[];
    tests: TraceTest[];
    covered: boolean;
    /** Present when the requirement has selected criteria (ADR-0086). */
    criteria?: TraceCriterion[];
  }[];
  risks: {
    id: string;
    title: string | null;
    gxp_risk: string | null;
    requirement_refs: string[];
    tests: TraceTest[];
    covered: boolean;
  }[];
  uncovered_requirements: string[];
  uncovered_high_medium: string[];
  /** `REQ-01:AC02` — selected criteria no live test names (ADR-0086); a note, not a gate. */
  uncovered_criteria?: string[];
  unlinked_tests: string[];
  counts: Record<string, number>;
}

/** Gate 1 with named approvers (ADR-0055). */
export type RiskApprovalOut = components["schemas"]["RiskApprovalOut"];
/** The ordered final sign-off (ADR-0059). */
export type SignoffOut = components["schemas"]["SignoffOut"];
export type SignoffStepOut = components["schemas"]["SignoffStepOut"];

/** A run document — the plan or the report — with its version history and
 *  review state (ADR-0056). */
export type ValidationDocumentOut = components["schemas"]["ValidationDocumentOut"];
export type DocumentMarkOut = components["schemas"]["DocumentMarkOut"];
export type DocumentVersionOut = components["schemas"]["DocumentVersionOut"];
export type DocumentVersionTextOut = components["schemas"]["DocumentVersionTextOut"];
export type DocumentKind = "requirements" | "risk_report" | "plan" | "test_plan" | "report";
/** What can be downloaded or exported as .md / .pdf. */
export type ArtefactKind = "plan" | "report" | "requirements" | "test_plan" | "risk_report";
export type DocumentReviewStatus = "draft" | "in_review" | "approved";
export type PatchRiskIn = components["schemas"]["PatchRiskIn"];

/** A risk added by hand before gate 1 — a direct rating or ITQRA buckets. */
export type ManualRiskIn = components["schemas"]["ManualRiskIn"];

export interface TestStep {
  n: number;
  action: string;
  expected: string;
}

/** One automatic check: a read-only VQL query with what its result must
 *  satisfy, or a permission check (ADR-0091) — a test role the run's profile
 *  maps to a vault user, the permission name and the actions expected. */
export interface ApiAssertion {
  description?: string | null;
  vql?: string | null;
  expect?: { kind: string; value?: number | string | null; field?: string | null } | null;
  role?: string | null;
  permission?: string | null;
  expect_permissions?: {
    read?: boolean | null;
    edit?: boolean | null;
    create?: boolean | null;
    delete?: boolean | null;
  } | null;
  /** ADR-0096: a configuration check — one configuration record, read-only. */
  component?: string | null;
  record?: string | null;
  path?: string | null;
  expect_config?: {
    kind: "exists" | "equals" | "contains" | "not_contains";
    value?: string | number | boolean | null;
  } | null;
  /** ADR-0096 §7: a profile permission check — what a security profile's
   *  permission sets grant on an object, read from the security configuration. */
  security_profile?: string | null;
  object_name?: string | null;
}

export interface EvidenceOut {
  evidence_id: string;
  run_id: string;
  test_id: string;
  filename: string;
  content_type: string | null;
  size_bytes: number;
  sha256: string;
  note: string | null;
  uploaded_by_user_id: string | null;
  uploaded_by_display_name: string | null;
  /** The test's attempt the file was attached to (ADR-0079 §5.7). */
  attempt?: number;
  /** The step the file belongs to (1-based), or null for the whole test (ADR-0082). */
  step_no?: number | null;
  created_at_utc: string;
}

export interface TestOut {
  /** The environment the test belongs to (ADR-0079 §5.6); null without. */
  environment?: string | null;
  /** Attempts (ADR-0079 §5.7): the current one, and every earlier one kept. */
  attempt?: number;
  attempts?: Record<string, unknown>[];
  /** Independent review (ADR-0079 §5.7). */
  review_required?: boolean;
  awaiting_review?: boolean;
  reviewed_by_user_id?: string | null;
  reviewed_by_display_name?: string | null;
  reviewed_at_utc?: string | null;
  review_signature_id?: string | null;
  review_outcome?: "confirmed" | "returned" | null;
  review_note?: string | null;
  test_id: string;
  run_id: string;
  seq: number;
  code: string;
  title: string;
  objective: string | null;
  risk_ref: string | null;
  /** `REQ-01, REQ-02:AC03` — the requirements and criteria the test verifies (ADR-0086). */
  requirement_ref: string | null;
  /** The same as pieces: `REQ-01`, `REQ-02:AC03`. */
  requirement_refs: string[];
  gxp_risk: string | null;
  preconditions: string | null;
  steps: TestStep[];
  test_data: { item: string; value?: string | null }[];
  /** The records the test needs, as the AI read them (ADR-0073 §9): per Vault
   *  object, how many, with which attributes — and, when it names them, the
   *  records themselves (field → text, ADR-0078). The run sums them per object
   *  and matches them to the profile's test-data pipelines. */
  test_data_needs: {
    object: string;
    count: number;
    attributes: Record<string, unknown>;
    records?: Record<string, string>[];
  }[];
  reused_from: string | null;
  mode: TestMode;
  mode_rationale: string | null;
  api_assertions: ApiAssertion[];
  status: TestStatus;
  assignee_user_id: string | null;
  assignee_email: string | null;
  assignee_display_name: string | null;
  assigned_at_utc: string | null;
  executed_by_user_id: string | null;
  executed_by_display_name: string | null;
  executed_at_utc: string | null;
  step_results: Record<string, unknown>[] | null;
  observed_result: string | null;
  duration_ms: number | null;
  evidence: EvidenceOut[];
  /** "ai" = written by the test author; "manual" = written by a person;
   *  "standard" = an environment's standard test (ADR-0079). */
  origin: "ai" | "manual" | "standard";
  created_by_user_id: string | null;
  /** Resolved at view time (blank after erasure); never stored on the test. */
  created_by_display_name: string | null;
  withdrawn_reason: string | null;
  withdrawn_at_utc: string | null;
  withdrawn_by_user_id: string | null;
  withdrawn_by_display_name: string | null;
  /** Set while the test is accepted without execution (ADR-0057). */
  acceptance_category?: string | null;
  acceptance_reason?: string | null;
  acceptance_prior_status?: string | null;
  accepted_by_user_id?: string | null;
  accepted_by_display_name?: string | null;
  accepted_at_utc?: string | null;
  acceptance_signature_id?: string | null;
  acceptance_qa_user_id?: string | null;
  acceptance_qa_display_name?: string | null;
  acceptance_qa_at_utc?: string | null;
  acceptance_qa_signature_id?: string | null;
  /** The risk is not Low/N/A: QA must counter-sign before it counts. */
  acceptance_qa_required?: boolean;
  /** Counts toward coverage now. */
  acceptance_counts?: boolean;
  created_at_utc: string;
  updated_at_utc: string;
}

/** A test written by hand — numbered TC-nn and attributed by the server. */
export type CreateTestIn = components["schemas"]["CreateTestIn"];

/** A defect (ADR-0079 §5.8): kept across evaluations, with its decision,
 *  the decision's history, and who raised it when a person did. */
export type DefectOut = components["schemas"]["DefectOut"];
export type DefectDecision = components["schemas"]["DecideDefectIn"]["decision"];
export type RaiseDefectIn = components["schemas"]["RaiseDefectIn"];

export interface EvaluationRisk {
  id: string;
  title: string;
  gxp_risk: string;
  tests: string[];
  passed_tests: string[];
  failed_tests: string[];
  /** Withdrawn tests that pointed at this risk — they cover nothing. */
  withdrawn_tests?: string[];
  /** Accepted without execution (ADR-0057): all, those that count, and those
   *  waiting for the QA counter-signature. Absent on older evaluations. */
  accepted_tests?: string[];
  accepted_counted?: string[];
  accepted_pending_qa?: string[];
  covered: boolean;
}

export interface EvaluationAcceptance {
  code: string;
  test_id: string;
  risk_ref: string | null;
  gxp_risk: string | null;
  category: string | null;
  accepted_by_user_id: string | null;
  accepted_at_utc: string | null;
  signature_id: string | null;
  qa_required: boolean;
  qa_user_id: string | null;
  qa_signed_at_utc: string | null;
  qa_signature_id: string | null;
  counted: boolean;
}

export interface Evaluation {
  tests: {
    total: number;
    passed: number;
    failed: number;
    blocked: number;
    open: number;
    open_codes: string[];
    manual: number;
    automatic: number;
    /** Absent on evaluations computed before withdrawal existed. */
    withdrawn?: number;
    withdrawn_codes?: string[];
    /** Accepted without execution — never part of `passed` (ADR-0057). */
    accepted?: number;
    accepted_codes?: string[];
    accepted_counted?: number;
    accepted_pending_qa?: string[];
  };
  risks: EvaluationRisk[];
  acceptances?: EvaluationAcceptance[];
  uncovered_high_medium: string[];
  defects: {
    code: string;
    severity: string;
    test_code: string | null;
    title: string;
    status?: string;
    origin?: string;
    decision?: string;
  }[];
  evidence_count_by_test: Record<string, number>;
  computed_readiness: Readiness;
  model_readiness: Readiness | null;
  readiness_mismatch: boolean;
  computed_at_utc: string;
}

export type StageOut = components["schemas"]["StageOut"];

/** One in-scope risk against its test-strategy row (ADR-0079 §5.5). */
export interface StrategyCheck {
  risk: string;
  rating: string;
  category: string | null;
  modes: string[];
  min_tests: number;
  independent_review: boolean;
  tests: string[];
  problems: string[];
  ok: boolean;
}

export interface RunOut {
  run_id: string;
  title: string;
  vault_product: string;
  change_ref: string | null;
  /** The Azure DevOps change request the run was picked from (ADR-0080). */
  change_work_item_id?: number | null;
  change_work_item_url?: string | null;
  /** Every RfC the run was picked from, in order. */
  change_work_items?: { id: number; title: string; url: string | null }[];
  /** Which inputs the run needs (ADR-0081): rfc, urs, current, upcoming →
   *  "required" / "optional". */
  inputs?: Record<string, string>;
  description: string | null;
  /** The validation profile the run was created with (ADR-0073): its id and
   *  key, and the name, description and revision as chosen — what the run
   *  shows. Null = none. Changeable with a reason until gate 1. */
  profile_id?: string | null;
  profile_key?: string | null;
  profile_name?: string | null;
  profile_description?: string | null;
  profile_revision?: number | null;
  status: RunStatus;
  stage_index: number;
  stages: string[];
  /** The risk assessment (and risk-item extraction) running in the
   *  background for this run (ADR-0077); null when there is none. */
  assessment_in_progress?: AssessmentProgressOut | null;
  ai_connection_id: string;
  deployment: string;
  veeva_connection_id: string | null;
  blob_connection_id: string | null;
  /** The optional Requirements Specification (ADR-0060); null = the run
   *  skipped the stage (single run view). */
  requirements?: Requirement[] | null;
  requirements_source?: Record<string, unknown> | null;
  /** ADR-0095: requirements the match rated below the threshold, kept aside
   *  (ids C-nn, most relevant first). */
  requirement_candidates?: Requirement[] | null;
  /** ADR-0096: the test vault's security configuration, summarised. */
  security_config?: SecurityConfigSummary | null;
  /** What the run's risk assessment read: documents per side, the change
   *  request's reference and hash, the Blob connection's name. */
  assessment_inputs?: AssessmentInputs | null;
  /** The Blob connection the URS was read from, by name. */
  requirements_connection_name?: string | null;
  /** The AI is making the manual tests automatic in the background. */
  tests_automating?: TestsAutomating | null;
  requirements_prompt_version?: string | null;
  requirements_inference_id?: string | null;
  requirements_confirmed_at_utc?: string | null;
  requirements_confirmed_by_user_id?: string | null;
  requirements_confirmed_by_display_name?: string | null;
  requirements_document?: ValidationDocumentOut | null;
  /** The Test Plan: AI narrative provenance; its matrix is computed. */
  test_plan_prompt_version?: string | null;
  test_plan_inference_id?: string | null;
  test_plan_document?: ValidationDocumentOut | null;
  /** The Risk Assessment Report (ADR-0079), when the run's method produces it. */
  risk_report_document?: ValidationDocumentOut | null;
  /** The requirement → risk → test matrix computed now (single run view). */
  traceability?: Traceability | null;
  risk_assessment_id: string | null;
  risks: RiskItem[] | null;
  risks_prompt_version: string | null;
  risks_inference_id: string | null;
  risks_confirmed_at_utc: string | null;
  /** Gate 1's approval state; null in lists. */
  risk_approval?: RiskApprovalOut | null;
  plan_md: string | null;
  plan_version: number;
  plan_prompt_version: string | null;
  plan_inference_id: string | null;
  plan_saved_blob_path: string | null;
  tests_prompt_version: string | null;
  tests_inference_id: string | null;
  tests_approved_at_utc: string | null;
  evaluation: Evaluation | null;
  readiness: Readiness | null;
  report_md: string | null;
  report_prompt_version: string | null;
  report_inference_id: string | null;
  report_saved_blob_path: string | null;
  /** The plan and the report as versioned documents (single run view). */
  plan_document?: ValidationDocumentOut | null;
  report_document?: ValidationDocumentOut | null;
  /** The ordered final sign-off (ADR-0059); null when the run names nobody
   *  to sign after the owner, in lists, and before the report. */
  signoff?: SignoffOut | null;
  /** It is the caller's turn to sign the final package (lists too). */
  signoff_waiting?: boolean;
  signature_id: string | null;
  approved_at_utc: string | null;
  approved_by_user_id: string | null;
  approved_by_display_name: string | null;
  rejection_reason: string | null;
  /** Cancellation (ADR-0062), set while `status` is `cancelled`: when, by
   *  whom, why (single run view), the signature record, and the stage a
   *  reopen returns to. */
  cancelled_at_utc?: string | null;
  cancelled_by_user_id?: string | null;
  cancelled_by_display_name?: string | null;
  cancel_reason?: string | null;
  cancel_signature_id?: string | null;
  cancelled_from_stage?: RunStatus | null;
  /** The meaning proposed for the owner's cancel signature (owner only). */
  cancel_default_meaning?: string | null;
  /** Closed without verification (ADR-0088): set while `status` is `closed`. */
  closed_at_utc?: string | null;
  closed_by_user_id?: string | null;
  closed_by_display_name?: string | null;
  close_reason?: string | null;
  close_signature_id?: string | null;
  close_default_meaning?: string | null;
  /** The owner may close the run without verification now (ADR-0088). */
  may_close_early?: boolean;
  last_error: string | null;
  ado_work_item_id: number | null;
  ado_work_item_url: string | null;
  owner_user_id: string | null;
  owner_email: string | null;
  owner_display_name: string | null;
  is_owner: boolean;
  /** The caller's participant role on the run (ADR-0054); null for the owner
   *  and for anyone else who can see it. */
  my_role: ParticipantRole | null;
  /** Owner or co-editor: may change the run's content. */
  may_edit: boolean;
  /** The caller's own user id ("My risks", the assignee's per-item edit). */
  viewer_user_id?: string | null;
  /** Whether the run names a QA approver (single run view). Without one, an
   *  acceptance on a High/Medium risk never counts (ADR-0057). */
  has_qa_approver?: boolean;
  /** For the run's QA approver: acceptances waiting for their counter-signature. */
  qa_acceptances_waiting?: number;
  /** Per AI step, the client guidance it last received (ADR-0065); a step not
   *  yet run is absent. */
  guidance_log?: Record<string, components["schemas"]["GuidanceLogStepOut"]>;
  /** The validation method the run pinned (ADR-0079); single run view only. */
  method?: components["schemas"]["EffectiveMethodOut"] | null;
  /** Per existing document, the download formats the method allows in its
   *  current state (ADR-0079 §5.1); single run view only. */
  export_formats?: Record<string, string[]>;
  /** The method's test strategy against the tests, per risk (ADR-0079). */
  test_strategy?: StrategyCheck[];
  /** The caller may review an execution they did not run (ADR-0079). */
  viewer_may_review?: boolean;
  /** The run's environments as stages (ADR-0079 §5.6). */
  environment_stages?: StageOut[];
  test_count: number;
  tests: TestOut[];
  defects: DefectOut[];
  created_at_utc: string;
  updated_at_utc: string;
}

export interface RunListOut {
  items: RunOut[];
}

/** People named on a run (ADR-0054): one role each, never the owner. */
export const PARTICIPANT_ROLES = ["editor", "reviewer", "approver", "qa_approver"] as const;
export type ParticipantRole = (typeof PARTICIPANT_ROLES)[number];
export type ParticipantIn = components["schemas"]["ParticipantIn"];
export type ParticipantOut = components["schemas"]["ParticipantOut"];
export type ParticipantListOut = components["schemas"]["ParticipantListOut"];

export const PARTICIPANT_ROLE_LABEL: Record<ParticipantRole, string> = {
  editor: "Co-editor",
  reviewer: "Reviewer",
  approver: "Approver",
  qa_approver: "QA approver",
};

export const PARTICIPANT_ROLE_HELP: Record<ParticipantRole, string> = {
  editor: "Changes the run's content like you do. Cannot confirm the gates, sign off or delete.",
  reviewer: "Opens the run to review it. Cannot change it.",
  approver: "Opens the run to approve it. Cannot change it.",
  qa_approver: "The run's one named QA approver. Opens it; cannot change it.",
};

export interface MyTestOut {
  test: TestOut;
  run_title: string;
  vault_product: string;
  change_ref: string | null;
  run_status: RunStatus;
}

export interface MyTestListOut {
  items: MyTestOut[];
}

export interface CreateRunIn {
  title: string;
  /** Required unless the chosen profile's default fills it. */
  vault_product?: string | null;
  change_ref?: string | null;
  /** With an Azure DevOps change source set, the RfC picked from it —
   *  required; its description goes in `description` (ADR-0080). */
  change_work_item_id?: number | null;
  /** Several RfCs validated together; replaces `change_work_item_id`. */
  change_work_item_ids?: number[] | null;
  description?: string | null;
  /** An approved AI model (ADR-0064); omitted → the default one. */
  ai_model_id?: string | null;
  /** Legacy: must match an approved entry, else 403 `ai_model_not_approved`. */
  ai_connection_id?: string | null;
  deployment?: string | null;
  /** Omitted → the Validation defaults (ADR-0058); set only from Advanced. */
  veeva_connection_id?: string | null;
  blob_connection_id?: string | null;
  risk_assessment_id?: string | null;
  /** The key of an active validation profile (ADR-0073): its current versions
   *  are pinned on the run and its defaults fill what is not given. */
  profile?: string | null;
}

export interface UpdateTestIn {
  title?: string;
  objective?: string | null;
  preconditions?: string | null;
  risk_ref?: string | null;
  requirement_ref?: string | null;
  /** A list the server joins into `requirement_ref` (ADR-0086); wins over the text. */
  requirement_refs?: string[] | null;
  gxp_risk?: string | null;
  mode?: TestMode;
  mode_rationale?: string | null;
  steps?: TestStep[];
  test_data?: { item: string; value?: string | null }[];
  api_assertions?: ApiAssertion[];
  /** `""` unassigns. */
  assignee_user_id?: string;
}

export interface ManualResultIn {
  step_results: { n: number; result: "pass" | "fail" | "na"; note?: string | null }[];
  observed_result: string;
  verdict: "passed" | "failed" | "blocked";
}

export interface ExportIn {
  artefact: ArtefactKind;
  fmt: "md" | "pdf";
  container: string;
  path: string;
  blob_connection_id?: string | null;
}

const BASE = "/api/v1/validation";
const enc = encodeURIComponent;

export function listRuns(token: string, opts: { background?: boolean } = {}): Promise<RunListOut> {
  return apiFetch<RunListOut>(`${BASE}/runs`, { token, background: opts.background });
}

/** `background`: a poll (sends `X-Daquma-Background: 1`, not activity). */
export function getRun(
  token: string,
  runId: string,
  opts: { background?: boolean } = {},
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}`, { token, background: opts.background });
}

export function createRun(token: string, input: CreateRunIn): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs`, { method: "POST", token, json: input });
}

/** Container names of the run's Blob account (its own, else the default) —
 *  for whoever may edit the run (ADR-0064). */
export function listRunBlobContainers(
  token: string,
  runId: string,
): Promise<{ items: ContainerSummary[] }> {
  return apiFetch<{ items: ContainerSummary[] }>(`${BASE}/runs/${enc(runId)}/blob/containers`, {
    token,
  });
}

/** File names under a prefix of the run's Blob account — names, never content. */
export function listRunBlobFiles(
  token: string,
  runId: string,
  container: string,
  prefix?: string | null,
): Promise<{ items: BlobSummary[] }> {
  const params = new URLSearchParams({ container });
  if (prefix) params.set("prefix", prefix);
  return apiFetch<{ items: BlobSummary[] }>(
    `${BASE}/runs/${enc(runId)}/blob/files?${params.toString()}`,
    { token },
  );
}

/** Set or clear (null) the run's validation profile (ADR-0073) — owner or
 *  co-editor, with a reason, before gate 1 only (409
 *  `validation_invalid_transition` after). The old profile's pins end and the
 *  new profile's current versions are pinned. */
export function setRunProfile(
  token: string,
  runId: string,
  profile: string | null,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/profile`, {
    method: "PUT",
    token,
    json: { profile, reason },
  });
}

export function deleteRun(token: string, runId: string, reason: string): Promise<void> {
  return apiFetch<void>(`${BASE}/runs/${enc(runId)}?reason=${enc(reason)}`, {
    method: "DELETE",
    token,
  });
}

export function listParticipants(token: string, runId: string): Promise<ParticipantListOut> {
  return apiFetch<ParticipantListOut>(`${BASE}/runs/${enc(runId)}/participants`, { token });
}

export function addParticipant(
  token: string,
  runId: string,
  input: ParticipantIn,
): Promise<ParticipantListOut> {
  return apiFetch<ParticipantListOut>(`${BASE}/runs/${enc(runId)}/participants`, {
    method: "POST",
    token,
    json: input,
  });
}

export function removeParticipant(
  token: string,
  runId: string,
  userId: string,
): Promise<ParticipantListOut> {
  return apiFetch<ParticipantListOut>(`${BASE}/runs/${enc(runId)}/participants/${enc(userId)}`, {
    method: "DELETE",
    token,
  });
}

export function attachAssessment(
  token: string,
  runId: string,
  riskAssessmentId: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/attach-assessment`, {
    method: "POST",
    token,
    json: { risk_assessment_id: riskAssessmentId },
  });
}

export function updateRisks(token: string, runId: string, items: RiskItem[]): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/risks`, {
    method: "PUT",
    token,
    json: { items },
  });
}

/** Add a risk the assessment missed (before gate 1; numbered M-nn). */
export function addManualRisk(token: string, runId: string, input: ManualRiskIn): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/risks/manual`, {
    method: "POST",
    token,
    json: input,
  });
}

/** Remove a manually added risk (before gate 1). Extracted items are excluded, not removed.
 *  With `version`, refused (409) if someone changed the item since. */
export function removeManualRisk(
  token: string,
  runId: string,
  riskId: string,
  version?: number,
): Promise<RunOut> {
  const q = version === undefined ? "" : `?version=${version}`;
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/risks/manual/${enc(riskId)}${q}`, {
    method: "DELETE",
    token,
  });
}

/** Change one risk item (ADR-0055); 409 `validation_risk_stale` if it changed since. */
export function patchRisk(
  token: string,
  runId: string,
  riskId: string,
  input: PatchRiskIn,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/risks/${enc(riskId)}`, {
    method: "PATCH",
    token,
    json: input,
  });
}

/** Make the owner or a person named on the run responsible for a risk; null unassigns. */
export function assignRisk(
  token: string,
  runId: string,
  riskId: string,
  userId: string | null,
  version?: number,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/risks/${enc(riskId)}/assignee`, {
    method: "PUT",
    token,
    json: { user_id: userId, version: version ?? null },
  });
}

/** Gate 1 with approvers: the owner submits the risks for approval. */
export function submitRisks(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/risks/submit`, { method: "POST", token });
}

/** A named approver signs the submitted risks (electronic signature). */
export function approveRisks(token: string, runId: string, meaning: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/risks/approve`, {
    method: "POST",
    token,
    json: { meaning },
  });
}

/** A named approver sends the submitted risks back to editing. */
export function refuseRisks(token: string, runId: string, reason: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/risks/refuse`, {
    method: "POST",
    token,
    json: { reason },
  });
}

/** GATE 1 — freezes the risk items and drafts the plan in one call. */
export function confirmRisks(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/confirm-risks`, {
    method: "POST",
    token,
  });
}

export function generatePlan(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/generate-plan`, {
    method: "POST",
    token,
  });
}

export function updatePlan(
  token: string,
  runId: string,
  planMd: string,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/plan`, {
    method: "PUT",
    token,
    json: { plan_md: planMd, reason },
  });
}

const docUrl = (runId: string, kind: DocumentKind) =>
  `${BASE}/runs/${enc(runId)}/documents/${kind}`;

/** Edit the plan or the report: a new version with a reason. With `version`,
 *  refused (409 `validation_document_stale`) if someone saved a newer one. */
export function updateDocument(
  token: string,
  runId: string,
  kind: DocumentKind,
  bodyMd: string,
  reason: string,
  version?: number,
): Promise<RunOut> {
  return apiFetch<RunOut>(docUrl(runId, kind), {
    method: "PUT",
    token,
    json: { body_md: bodyMd, reason, version: version ?? null },
  });
}

/** One version of a document, with its text. */
export function getDocumentVersion(
  token: string,
  runId: string,
  kind: DocumentKind,
  version: number,
): Promise<DocumentVersionTextOut> {
  return apiFetch<DocumentVersionTextOut>(`${docUrl(runId, kind)}/versions/${version}`, {
    token,
  });
}

/** An editor submits the current version for review. */
export function submitDocument(token: string, runId: string, kind: DocumentKind): Promise<RunOut> {
  return apiFetch<RunOut>(`${docUrl(runId, kind)}/submit`, { method: "POST", token });
}

/** A named reviewer records "reviewed" (not a signature). */
export function reviewDocument(
  token: string,
  runId: string,
  kind: DocumentKind,
  version: number,
  comment: string | null,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${docUrl(runId, kind)}/review`, {
    method: "POST",
    token,
    json: { version, comment },
  });
}

/** A named approver signs the version in review (electronic signature). */
export function approveDocument(
  token: string,
  runId: string,
  kind: DocumentKind,
  version: number,
  meaning: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${docUrl(runId, kind)}/approve`, {
    method: "POST",
    token,
    json: { version, meaning },
  });
}

/** A named reviewer or approver returns the document to draft. */
export function returnDocument(
  token: string,
  runId: string,
  kind: DocumentKind,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${docUrl(runId, kind)}/return`, {
    method: "POST",
    token,
    json: { reason },
  });
}

export function generateTests(
  token: string,
  runId: string,
  existingTestCases?: string | null,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/generate-tests`, {
    method: "POST",
    token,
    json: { existing_test_cases: existingTestCases ?? null },
  });
}

export function updateTest(
  token: string,
  runId: string,
  testId: string,
  input: UpdateTestIn,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}`, {
    method: "PATCH",
    token,
    json: input,
  });
}

/** Add a test written by hand (next free TC-nn), before or after gate 2. */
export function createTest(token: string, runId: string, input: CreateTestIn): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests`, {
    method: "POST",
    token,
    json: input,
  });
}

/** Remove a test with a reason: deleted before gate 2, withdrawn after it.
 *  An executed test is refused (409). */
export function removeTest(
  token: string,
  runId: string,
  testId: string,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}?reason=${enc(reason)}`, {
    method: "DELETE",
    token,
  });
}

/** Accept a test without executing it (ADR-0057) — an electronic signature:
 *  a 401 `signature_reauth_required` means re-authenticate and retry. */
export function acceptTest(
  token: string,
  runId: string,
  testId: string,
  category: AcceptanceCategory,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/accept`, {
    method: "POST",
    token,
    json: { category, reason },
  });
}

/** The run's QA approver counter-signs an acceptance (electronic signature). */
export function countersignAcceptance(
  token: string,
  runId: string,
  testId: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/accept/qa-sign`, {
    method: "POST",
    token,
  });
}

/** An editor revokes an acceptance; the test returns to its prior status. */
export function revokeAcceptance(
  token: string,
  runId: string,
  testId: string,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/accept/revoke`, {
    method: "POST",
    token,
    json: { reason },
  });
}

/** GATE 2 — every manual test has a tester; execution opens. */
export function approveTests(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/approve-tests`, {
    method: "POST",
    token,
  });
}

export function executeAutomaticTest(
  token: string,
  runId: string,
  testId: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/execute`, {
    method: "POST",
    token,
  });
}

export function executeAllAutomatic(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/execute-automatic`, {
    method: "POST",
    token,
  });
}

/** The run owner records a manual result on the tester's behalf. */
export function ownerManualResult(
  token: string,
  runId: string,
  testId: string,
  input: ManualResultIn,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/result`, {
    method: "POST",
    token,
    json: input,
  });
}

export function ownerAddEvidence(
  token: string,
  runId: string,
  testId: string,
  file: File,
  note?: string,
  step?: number,
): Promise<RunOut> {
  const form = new FormData();
  form.append("file", file);
  if (note) form.append("note", note);
  if (step) form.append("step", String(step));
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/evidence`, {
    method: "POST",
    token,
    body: form,
  });
}

export function evaluateRun(token: string, runId: string, allowOpenTests = false): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/evaluate`, {
    method: "POST",
    token,
    json: { allow_open_tests: allowOpenTests },
  });
}

/** GATE 3 — electronic signature. The token must carry a fresh `auth_time`;
 *  a 401 `signature_reauth_required` means re-authenticate and retry. */
export function approveRun(token: string, runId: string, meaning: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/approve`, {
    method: "POST",
    token,
    json: { meaning },
  });
}

export function rejectRun(token: string, runId: string, reason: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/reject`, {
    method: "POST",
    token,
    json: { reason },
  });
}

/** Ordered sign-off (ADR-0059): the caller signs their step — owner first,
 *  then reviewers, approvers, the QA approver. Electronic signature: a 401
 *  `signature_reauth_required` means re-authenticate and retry. */
export function signSignoff(token: string, runId: string, meaning: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/signoff/sign`, {
    method: "POST",
    token,
    json: { meaning },
  });
}

/** Ordered sign-off: a step holder rejects the package with a reason. */
export function rejectSignoff(token: string, runId: string, reason: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/signoff/reject`, {
    method: "POST",
    token,
    json: { reason },
  });
}

export function reopenRun(token: string, runId: string, reason: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/reopen`, {
    method: "POST",
    token,
    json: { reason },
  });
}

/** The owner cancels a run that is not approved (ADR-0062). Electronic
 *  signature: a 401 `signature_reauth_required` means re-authenticate and
 *  retry. The run becomes read-only. */
export function cancelRun(
  token: string,
  runId: string,
  meaning: string,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/cancel`, {
    method: "POST",
    token,
    json: { meaning, reason },
  });
}

/** The owner closes an assessed run whose confirmed risks are all Low or N/A
 *  (ADR-0088): an electronic signature with its meaning, plus a reason. */
export function closeWithoutVerification(
  token: string,
  runId: string,
  meaning: string,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/close-without-verification`, {
    method: "POST",
    token,
    json: { meaning, reason },
  });
}

export type ChangeStatementOut = components["schemas"]["ChangeStatementOut"];

/** The text of release documents as one change statement for a new run's
 *  description (ADR-0088): no AI, nothing stored. */
export function extractChangeStatement(token: string, files: File[]): Promise<ChangeStatementOut> {
  const form = new FormData();
  for (const f of files) form.append("files", f);
  return apiFetch<ChangeStatementOut>(`${BASE}/change-statement/extract`, {
    method: "POST",
    token,
    body: form,
  });
}

/** The owner reopens a cancelled run to the stage it was in — a plain
 *  audited act with a reason, not a signature (ADR-0062). */
export function reopenCancelledRun(token: string, runId: string, reason: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/cancel/reopen`, {
    method: "POST",
    token,
    json: { reason },
  });
}

export function exportArtefact(token: string, runId: string, input: ExportIn): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/export`, {
    method: "POST",
    token,
    json: input,
  });
}

/** Direct download URL (a run document, md / pdf). Fetch with the bearer token
 *  and hand the blob to the browser — see `downloadArtefact`. */
export function artefactUrl(runId: string, artefact: ArtefactKind, fmt: "md" | "pdf"): string {
  return `${BASE}/runs/${enc(runId)}/artefact/${artefact}.${fmt}`;
}

export async function downloadArtefact(
  token: string,
  runId: string,
  artefact: ArtefactKind,
  fmt: "md" | "pdf",
): Promise<Blob> {
  const response = await fetch(artefactUrl(runId, artefact, fmt), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);
  return response.blob();
}

export async function downloadEvidence(token: string, evidenceId: string): Promise<Blob> {
  const response = await fetch(`${BASE}/evidence/${enc(evidenceId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);
  return response.blob();
}

export interface RunAssessmentInRunIn {
  blob_connection_id?: string | null;
  current: { container: string; path: string }[];
  upcoming: { container: string; path: string }[];
  title?: string | null;
}

/** Stage 1 without leaving the run: read the picked documents from Blob and
 *  start the risk assessment (ADR-0077). Answers 202 with the run; its passes
 *  and the risk-item extraction run in the background — poll `getRun` while
 *  `assessment_in_progress` is set. */
export function runAssessmentInRun(
  token: string,
  runId: string,
  input: RunAssessmentInRunIn,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/run-assessment`, {
    method: "POST",
    token,
    json: input,
  });
}

// --- Requirements Specification and Test Plan (ADR-0060) -----------------

/** Read the customer's URS from the Validation default Blob storage (or the
 *  run's own) and extract its requirements — one model call. */
export function extractRequirements(
  token: string,
  runId: string,
  files: readonly { container: string; path: string }[],
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/requirements/extract`, {
    method: "POST",
    token,
    json: { files },
  });
}

/** Upload the customer's URS — one file or several (ADR-0094) — and load its
 *  text; no AI (ADR-0080). */
export function uploadRequirements(
  token: string,
  runId: string,
  files: readonly File[],
): Promise<RunOut> {
  const form = new FormData();
  for (const file of files) form.append("file", file);
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/requirements/upload`, {
    method: "POST",
    token,
    body: form,
  });
}

/** Match the loaded URS to the run's change requests: the AI keeps only the
 *  requirements the RfCs touch (ADR-0080). */
export function matchRequirements(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/requirements/match`, {
    method: "POST",
    token,
  });
}

/** One document an input step read, as recorded. */
export interface InputFile {
  filename?: string;
  container?: string;
  path?: string;
  sha256?: string;
  chars?: number;
  format?: string;
}

/** What the run's risk assessment read (its own record). */
export interface AssessmentInputs {
  assessment_id: string;
  title: string;
  created_at_utc: string;
  blob_connection_name?: string | null;
  current: InputFile[];
  upcoming: InputFile[];
  change?: { ref?: string | null; sha256?: string; chars?: number } | null;
}

/** ADR-0096: the test vault's security configuration on a run, as the run
 *  view shows it — never the records. */
export interface SecurityConfigSummary {
  fetched_at_utc?: string | null;
  vault_id?: string | null;
  vault_name?: string | null;
  /** The integration's own name (set when fetched); absent on older snapshots. */
  connection_name?: string | null;
  sha256?: string | null;
  components: Record<string, { status: "ok" | "refused"; count: number; error?: string }>;
}

/** ADR-0096: the AI writes the checks for one test, which becomes automatic;
 *  a 400 carries what blocks it. Read-only on the vault. */
export function automateTest(token: string, runId: string, testId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/automate`, {
    method: "POST",
    token,
  });
}

/** ADR-0096 §10: try every manual test that could be automatic, in the
 *  background; the run's `tests_automating` shows the progress. */
export function automateManualTests(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/automate-manual`, {
    method: "POST",
    token,
  });
}

/** The background job making the manual tests automatic (ADR-0096 §10). */
export interface TestsAutomating {
  total: number;
  done: number;
  current?: string | null;
  automatic: number;
}

/** One part of the run's security configuration, as read from the vault. */
export interface SecurityConfigRecords {
  component: string;
  status: string;
  count: number;
  error?: string | null;
  records: Record<string, unknown>[];
}

/** The records of one part, exactly as stored on the run (no vault call). */
export function getSecurityConfigRecords(
  token: string,
  runId: string,
  component: string,
): Promise<SecurityConfigRecords> {
  return apiFetch<SecurityConfigRecords>(
    `${BASE}/runs/${enc(runId)}/security-config/${enc(component)}`,
    { token },
  );
}

/** Read the test vault's security configuration (read-only) onto the run. */
export function fetchSecurityConfig(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/security-config/fetch`, {
    method: "POST",
    token,
  });
}

/** Rate the run's unrated requirements (a list imported by column, typed ones)
 *  against its change (ADR-0095): the ones below the threshold move to the
 *  lower-relevance list. AI, recorded. */
export function rateRequirements(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/requirements/rate`, {
    method: "POST",
    token,
  });
}

/** Add requirements the match rated below the relevance threshold (ADR-0095):
 *  each becomes the next REQ-nn, a new version of the Requirements
 *  Specification. */
export function acceptRequirementCandidates(
  token: string,
  runId: string,
  ids: string[],
  reason: string,
  version?: number,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/requirements/candidates/accept`, {
    method: "POST",
    token,
    json: { ids, reason, version: version ?? null },
  });
}

export type RequirementsImportPreview = components["schemas"]["RequirementsImportPreviewOut"];
export type CatalogueOut = components["schemas"]["RequirementCatalogueOut"];
export type CatalogueRowOut = components["schemas"]["RequirementCatalogueRowOut"];

/** The run's requirement catalogue (ADR-0083), searched by id or text, to add
 *  a requirement the match did not list. */
export function browseCatalogue(
  token: string,
  runId: string,
  q?: string,
  limit = 50,
): Promise<CatalogueOut> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (q) params.set("q", q);
  return apiFetch<CatalogueOut>(
    `${BASE}/runs/${enc(runId)}/requirements/catalogue?${params.toString()}`,
    { token },
  );
}

/** Read a CSV / XLSX requirement list and propose the column mapping
 *  (ADR-0079 §5.4); nothing is stored. */
export function previewRequirementsImport(
  token: string,
  runId: string,
  file: File,
): Promise<RequirementsImportPreview> {
  const form = new FormData();
  form.append("file", file);
  return apiFetch<RequirementsImportPreview>(
    `${BASE}/runs/${enc(runId)}/requirements/import/preview`,
    { method: "POST", token, body: form },
  );
}

/** Import the list mapped by column: REQ-nn with the customer's ids kept. */
export function importRequirements(
  token: string,
  runId: string,
  file: File,
  mapping: {
    id?: string | null;
    text: string | null;
    section?: string | null;
    gxp?: string | null;
  },
): Promise<RunOut> {
  const form = new FormData();
  form.append("file", file);
  form.append("text_column", mapping.text ?? "");
  if (mapping.id) form.append("id_column", mapping.id);
  if (mapping.section) form.append("section_column", mapping.section);
  if (mapping.gxp) form.append("gxp_column", mapping.gxp);
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/requirements/import`, {
    method: "POST",
    token,
    body: form,
  });
}

/** Send a failed or blocked test back for another attempt (ADR-0079 §5.7). */
export function retestTest(
  token: string,
  runId: string,
  testId: string,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/retest`, {
    method: "POST",
    token,
    json: { reason },
  });
}

/** An independent review of an execution, signed with a fresh token. */
export function reviewTest(
  token: string,
  runId: string,
  testId: string,
  input: { outcome: "confirmed" | "returned"; meaning: string; note?: string | null },
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/review`, {
    method: "POST",
    token,
    json: input,
  });
}

/** The owner signs off one environment (ADR-0079 §5.6): a fresh token. */
export function signStage(
  token: string,
  runId: string,
  environment: string,
  meaning: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/stages/${enc(environment)}/sign`, {
    method: "POST",
    token,
    json: { meaning },
  });
}

/** A signed stage summary with its signature block. */
export async function downloadStageSummary(
  token: string,
  runId: string,
  environment: string,
  fmt: "md" | "pdf",
): Promise<Blob> {
  const res = await fetch(`${BASE}/runs/${enc(runId)}/stages/${enc(environment)}/summary.${fmt}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
  return res.blob();
}

/** The traceability matrix as CSV, keyed by the customer's ids (audited). */
export async function downloadTraceabilityCsv(token: string, runId: string): Promise<Blob> {
  const res = await fetch(`${BASE}/runs/${enc(runId)}/traceability.csv`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

/** Save the whole requirement list with a reason: a new document version.
 *  `version` is the Requirements Specification version the edit was made on. */
export function updateRequirements(
  token: string,
  runId: string,
  items: RequirementIn[],
  reason: string,
  version?: number,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/requirements`, {
    method: "PUT",
    token,
    json: { items, reason, version: version ?? null },
  });
}

/** The owner confirms the requirements; the risk assessment then cites them. */
export function confirmRequirements(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/requirements/confirm`, {
    method: "POST",
    token,
  });
}

/** Draft or re-draft the Test Plan (AI narrative + computed matrix). */
export function generateTestPlan(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/generate-test-plan`, {
    method: "POST",
    token,
  });
}

// --- tester -------------------------------------------------------------

export function listMyTests(
  token: string,
  opts: { background?: boolean } = {},
): Promise<MyTestListOut> {
  return apiFetch<MyTestListOut>(`${BASE}/my-tests`, { token, background: opts.background });
}

export function startMyTest(token: string, testId: string): Promise<MyTestOut> {
  return apiFetch<MyTestOut>(`${BASE}/my-tests/${enc(testId)}/start`, {
    method: "POST",
    token,
  });
}

export function addMyEvidence(
  token: string,
  testId: string,
  file: File,
  note?: string,
  step?: number,
): Promise<MyTestOut> {
  const form = new FormData();
  form.append("file", file);
  if (note) form.append("note", note);
  if (step) form.append("step", String(step));
  return apiFetch<MyTestOut>(`${BASE}/my-tests/${enc(testId)}/evidence`, {
    method: "POST",
    token,
    body: form,
  });
}

export function submitMyResult(
  token: string,
  testId: string,
  input: ManualResultIn,
): Promise<MyTestOut> {
  return apiFetch<MyTestOut>(`${BASE}/my-tests/${enc(testId)}/result`, {
    method: "POST",
    token,
    json: input,
  });
}

/** Raise a defect no failed test raised (ADR-0079 §5.8; the run's method
 *  allows it). */
export function raiseDefect(token: string, runId: string, input: RaiseDefectIn): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/defects`, {
    method: "POST",
    token,
    json: input,
  });
}

/** Decide a defect with a justification. Accepting — and a decision the
 *  run's method marks signed — needs a fresh (step-up) token and a meaning. */
export function decideDefect(
  token: string,
  runId: string,
  defectId: string,
  input: { decision: DefectDecision; justification: string; meaning: string | null },
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/defects/${enc(defectId)}/decision`, {
    method: "POST",
    token,
    json: input,
  });
}

/** Create an Azure DevOps work item from a defect and link it back. */
export function pushDefectToAdo(
  token: string,
  runId: string,
  defectId: string,
  connectionId: string,
  itemType = "Bug",
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/defects/${enc(defectId)}/push-to-ado`, {
    method: "POST",
    token,
    json: { connection_id: connectionId, item_type: itemType },
  });
}

/** Push the whole run to Azure DevOps as one work item, optionally parented
 *  under an RfC / requirement work item. */
export function pushRunToAdo(
  token: string,
  runId: string,
  connectionId: string,
  opts: { parentWorkItemId?: number | null; itemType?: string } = {},
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/push-to-ado`, {
    method: "POST",
    token,
    json: {
      connection_id: connectionId,
      parent_work_item_id: opts.parentWorkItemId ?? null,
      item_type: opts.itemType ?? "Task",
    },
  });
}
